import { Injectable, OnDestroy } from '@angular/core'
import { Subscription } from 'rxjs/internal/Subscription'
import { first } from 'rxjs/operators'
import { SubSink } from 'subsink'
import { ApiService } from './api.service'
import { AuthService } from './auth.service'
import { FirestoreService2 } from './firestore.service2'
import { GlobalService } from './global.service'
import { NotifyService } from './notifications/notify.service'
import { chunkArray, use } from './utils'
import { XlsService } from './xls.service'

@Injectable({ providedIn: 'root' })
export class UserService implements OnDestroy {
  type: string
  nick: string
  apiUrl: string
  count: number
  program: any

  private subs = new SubSink()
  private segmentSubscription: Subscription = null
  private fss: FirestoreService2

  constructor(
    public g: GlobalService,
    private notify: NotifyService,
    private auth: AuthService,
    private xlsService: XlsService,
    private api: ApiService
  ) {
    this.listenUserUpdates()
  }

  ngOnDestroy() {
    this.subs.unsubscribe()
  }

  async updateUserEmail(user: any, userOldEmail: string) {
    const api = use<ApiService>(ApiService)
    api.post('users/userAction', { action: 'updateUserEmail', uid: user.uid, email: user.email }, true).pipe(first()).toPromise().then(updated => {
      if (updated && updated.hasOwnProperty('success') && !updated.success) {
        throw Error(updated.error.indexOf('already in') > -1 ? 'Este email já é cadastrado por outro usuário.' : updated.error)
      } else {
        api.post('users/changeEmailsInCollections', { oldEmail: userOldEmail, newEmail: user.email, nick: user.nick }, true).pipe(first()).toPromise().then(ok => {
          if (ok.success) this.notify.update('Coleções alteradas', 'btn-success', 3000)
          else if (ok.message || ok.error) this.notify.update(ok.message || ok.error, 'btn-danger', 3000)
        })
      }
    })
  }

  listenUserUpdates() {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    if (this.g.user && (this.g.user.uid || this.g.user.id)) this.subs.add(
      this.fss.get('users', this.g.user.uid || this.g.user.id).subscribe(user => {
        this.g.set('user', user)
        if (user.segment) this.listenUserSegment(user)
      })
    )
  }

  private listenUserSegment(user: any) {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    if (!this.segmentSubscription) {
      this.segmentSubscription = this.fss.get('segments', user.segment.id).subscribe(segment => this.g.set('segment', segment))
      this.subs.add(this.segmentSubscription)
    }
  }

  recalcBalance(user: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const api = use<ApiService>(ApiService)
      this.notify.update('Recalculando ...', 'btn-success', 3000)
      api.post('transactions/points', user, true).pipe(first()).toPromise().then((result) => {
        this.notify.update('Recálculo efectuado.', 'btn-success', 3000)
        console.log('# calculado')
        resolve(result)
      }, err => {
        console.log(err)
        reject(err.message)
      })
    })
  }

  sendAccountActivationEmail(data) {
    const api = use<ApiService>(ApiService)
    api.post('messaging/sendAccountActivation', data, true).pipe(first()).toPromise()
      .then(() => { this.notify.update('Emails enviados.', 'btn-success', 3000) })
    this.notify.update('A enviar emails ...', 'btn-success', 3000)
  }

  updateAllBalances(data) {
    const api = use<ApiService>(ApiService)
    api.post('db/action', { action: 'list', nick: this.g.nick, collection: 'users', api: true }, true).subscribe(async users => {
      for (const chunk of chunkArray(users, 250)) {
        await api.post(`transactions/updateAllBalances`, { nick: this.g.nick, data: chunk }, true).pipe(first()).toPromise().then()
      }
    })
  }

  // TODO PF/WG migrate to email templates and move to email.service
  sendWelcomeUsers(items) {
    const program = {
      name: this.g.program.name,
      email: this.g.program.email,
      sender: this.g.program.sender,
      url: this.g.program.url,
      imgHeader: this.g.program.images['emailHeader'],
      imgFooter: this.g.program.images['emailFooter'],
      subject: `${this.g.program.name} | Ativa a tua conta`,
      templateId: Number(this.g.program.emails.newAccount),
    }
    const api = use<ApiService>(ApiService)
    return api.post('messaging/sendMany', { items: items, program: program }, true).toPromise()
  }

  sendResetEmail(email: string) {
    const _program = {
      name: this.g.program.name,
      nick: this.g.nick,
      email: this.g.program.email,
      sender: this.g.program.sender,
      imgHeader: this.g.program.images['emailHeader'],
      imgFooter: this.g.program.images['emailFooter'],
      templateId: this.g.program.emails['resetPassword'],
      url: this.g.program.url
    }
    const body = {
      nick: this.g.nick,
      email: email,
      displayName: email.split('@')[0],
      program: _program
    }
    if (this.g.program.logins.lookup) {
      const api = use<ApiService>(ApiService)
      api.post(`open/firestoreAction`, {
        action: 'list',
        collection: 'prospects',
        nick: this.g.nick,
        queryArray: [['email', '==', email]]
      }, true).pipe(first()).toPromise().then(prospect => {
        let used = false
        if (prospect.length > 0) used = prospect[0].used
        if (prospect.length <= 0 || !used) {
          this.notify.update('Este email não está registado, por favor registe-se primeiro.', 'btn-danger', 3000)
        } else this.auth.resetPasswordEmail(body).then(link => console.log(link))
      })
    } else this.auth.resetPasswordEmail(body).then(link => console.log(link))
  }


  async createUserVisit(): Promise<void> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2);

    if (this.g.user && this.g.user.email && this.g.user.id) {
      try {
        const dt = new Date();
        dt.setUTCHours(0, 0, 0, 0);

        const logsToday = await this.fss.list(`logs`, { where: [['timestamp', '>=', dt.getTime()], ['type', '==', 'access'], ['email', '==', this.g.user.email]] })
          .pipe(first()).toPromise()

        if (!logsToday || logsToday.length === 0) {
          const { segments, allSegments } = await this.getUserSegmentsData()

          const visit = {
            device: this.g.device || 'Unknown',
            displayName: this.g.user.displayName,
            email: this.g.user.email,
            segment: this.g.user.segment || null,
            segments,
            allSegments,
            timestamp: new Date().getTime(),
            type: 'access'
          };

          await this.fss.add(`logs`, visit);
          const newlyAwardedBadges = await this.giveBadge()

          if (newlyAwardedBadges && newlyAwardedBadges.length > 0) {
            const currentUser = this.g.user
            const existingBadges = currentUser.badges ? [...currentUser.badges] : []

            const allBadges = [...existingBadges, ...newlyAwardedBadges]

            const updatedUser = {
              ...currentUser,
              badges: allBadges
            }
            this.g.set('user', updatedUser)
            console.log('Locally updated g.user with new badges for immediate UI update.')
          }
        }
      }
      catch (err) {
        console.error('Error in createUserVisit:', err)
      }
    }
  }

  private async getUserSegmentsData(): Promise<{ segments: any[] | null, allSegments: string }> {
    let userSegmentsObjects = null
    if (this.g.user.segments && this.g.user.segments.length > 0) {
      try {
        const segmentsPromises = this.g.user.segments.map(segmentId =>
          this.fss.get('segments', segmentId).pipe(first()).toPromise()
        )
        userSegmentsObjects = await Promise.all(segmentsPromises)
      } catch (error) {
        console.error('Erro ao carregar segmentos:', error)
      }
    }

    const allSegmentsList = []
    if (this.g.user.segment && this.g.user.segment.name) {
      allSegmentsList.push(this.g.user.segment.name)
    }
    if (userSegmentsObjects && userSegmentsObjects.length > 0) {
      userSegmentsObjects.forEach(seg => {
        if (seg && seg.name) allSegmentsList.push(seg.name)
      })
    }

    return { segments: userSegmentsObjects, allSegments: allSegmentsList.join(', ') }
  }

  /**
   * Checks for and awards login-based badges.
   * Returns an array of badges that were newly awarded in this call.
   */
  async giveBadge(): Promise<any[]> {
    if (!this.g.user || !this.g.user.id || !this.g.user.email) return []
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)

    const newBadgesAwardedThisRun: any[] = []

    try {

      const userAtStartOfBadgeCheck = { ...this.g.user }
      const currentUserBadges = userAtStartOfBadgeCheck.badges ? [...userAtStartOfBadgeCheck.badges] : []
      const awardedBadgeIds = new Set(currentUserBadges.map(b => b.id))

      const loginBadges = await this.fss.list(`badges`, { where: [['condition.script', '==', 'login'], ['active', '==', true]] }).pipe(first()).toPromise()

      if (!loginBadges || loginBadges.length === 0) return []

      const userAccessLogs = await this.fss.list(`logs`, { where: [['email', '==', userAtStartOfBadgeCheck.email], ['type', '==', 'access']] }).pipe(first()).toPromise()

      for (const loginBadge of loginBadges) {
        if (loginBadge && loginBadge.id && !awardedBadgeIds.has(loginBadge.id)) {
          if (userAccessLogs && userAccessLogs.length >= loginBadge.condition.value) {
            const newBadge = { ...loginBadge, receivedAt: new Date().toISOString() }
            newBadgesAwardedThisRun.push(newBadge)
          }
        }
      }

      if (newBadgesAwardedThisRun.length > 0) {
        const allBadgesForFirestore = [...currentUserBadges, ...newBadgesAwardedThisRun]
        await this.fss.update('users', userAtStartOfBadgeCheck.id, { badges: allBadgesForFirestore })
        console.log("Updated user badges in Firestore with new awards:", newBadgesAwardedThisRun.map(b => b.name || b.id).join(', '));
        return newBadgesAwardedThisRun
      }

      return []
    } catch (error) {
      console.error('Error in giveBadge:', error)
      return []
    }
  }

  async getPointsExpiring(users) {
    console.log(users)
    const d = new Date()
    const year = d.getFullYear()
    const month = d.getMonth()
    const beginDate = new Date(year - 1, month, 1)
    const endDate = new Date(year, month + 1, 1)
    const activeUsers = users.filter(o => o.active)
    const data = []
    const api = use<ApiService>(ApiService)
    this.subs.add(api.post(`db/action`, {
      action: 'list',
      nick: this.g.nick,
      collection: 'transactions',
      where: [['active', '==', true], ['code', '==', 'Pontos atribuidos']]
    }, true).subscribe(_points => {
      for (const user of activeUsers) {
        const points = _points.filter(o => o.email === user.email)
        let pointDate, credits = 0, expired = 0, userCredits = 0
        for (const point of points) {
          pointDate = new Date(point.orderDate)
          if (point.description !== 'Pontos expirados') {
            userCredits = userCredits + Number(point.orderPoints)
          }
          if (point.description !== 'Pontos expirados' && beginDate <= pointDate && pointDate < endDate) {
            credits = credits + Number(point.orderPoints)
          }
          if (point.description === 'Pontos expirados') {
            expired = expired + Number(Math.abs(point.orderPoints))
          }
        }
        if (expired > 0) {
          data.push({
            'user de rede': user.userCode,
            'nome': user.displayName,
            'email': user.email,
            'segmento': user.segment,
            'pontos atribuidos desde o início': userCredits,
            'pontos gastos desde o início': user.debits,
            'saldo antes de expirar': user.balance + expired,
            'pontos atribuidos nos últimos 12 meses': credits,
            'pontos expirados': expired
          })
        }
      }
    }))
    if (data.length > 0) this.xlsService.export('users', data)
  }

  async givePromotionalPoints(user, promoCode: string = null, manager: boolean = false): Promise<any> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    console.log('givePromotionalPoints', user.id)
    const d = new Date()
    if (!user.promoCodes && !manager) user.promoCodes = []
    if (user.promoCodes.includes(promoCode) && !manager) this.notify.update('Código já utilizado, por favor use outro!', 'btn-danger', 5000)
    else {
      if (this.g.program.logins.promos.some(promo => promo.name === promoCode)) {
        this.notify.update('Inseriu um código válido.', 'btn-success', 5000)
        for (const promo of this.g.program.logins.promos) {
          if (promo.name === promoCode && new Date(promo.endDt) >= d) {
            const points = [{
              beginDt: this.g.program.cashbacks.beginDt,
              cashbackId: promoCode,
              code: 'Pontos atribuidos',
              description: `Código promocional ${promoCode}`,
              displayName: user['displayName'],
              email: user['email'],
              nick: this.nick,
              orderDate: d.toISOString(),
              orderPoints: promo.points,
              orderEuros: promo.points,
              primaryCode: 'Pontos',
              programPriceMax: this.g.program.programPriceMax,
              segment: user['segment'],
              userPriceMax: this.g.program.userPriceMax,
              xRate: 1,
            }]

            const api = use<ApiService>(ApiService)
            await api.save('Points', 'transactions', points, 'id').then(async data => {
              if (!manager) user.promoCodes.push(promoCode)
              if (user.id) this.fss.update(`users`, user.id, { promoCodes: !manager ? user.promoCodes : [promoCode], createdAt: user.createdAt, createdBy: user.createdBy })
              this.notify.update(`Código promocional ${promoCode} usado. Pontos atribuidos.`, 'btn-success', 10000)
            })

          } else if (promo.name === promoCode && new Date(promo.endDt) < d) {
            this.notify.update('Data limite do código ultrapassada, por favor use outro!', 'btn-danger', 5000)
          }
        }
      } else this.notify.update('Código inválido, por favor use outro!', 'btn-danger', 5000)
    }
  }

  /**
   * Calculates the expiry date for points or euros
   * - Points: +1 month after +1 year (12-13 month buffer)
   * - Euros: December 31 of the same calendar year (intentional: euros earned at year-end expire in a few days)
   * @param orderDate Transaction date as a JS `Date` (UTC)
   * @param isEuros If true, calculates expiry for euros; otherwise for points
   * @returns UTC timestamp of the expiry date
   */
  calculateExpiryDate(orderDate: Date, isEuros = false): number {
    if (isEuros) {
      // Euros expire on Dec 31 of the same calendar year (intentional short expiry for late-year earnings)
      return Date.UTC(orderDate.getUTCFullYear(), 11, 31, 23, 59, 59, 999)
    }
    const nextMonthDate = new Date(Date.UTC(orderDate.getUTCFullYear(), orderDate.getUTCMonth() + 1, 1))
    return Date.UTC(nextMonthDate.getUTCFullYear() + 1, nextMonthDate.getUTCMonth(), 1)
  }
}
